import React from 'react';
import { Badge } from '../ui/badge';
import { CheckCircle2, ArrowRight, Beaker } from 'lucide-react';

const DENSITY_MATERIALS = [
  { name: 'Glass Wool', type: 'conventional', range: '24 – 96', width: '12%', offset: '0%' },
  { name: 'Rock Wool', type: 'conventional', range: '40 – 160', width: '20%', offset: '0%' },
  { name: 'Cork Panels', type: 'comparable', range: '180 – 260', width: '33%', offset: '22%' },
  { name: 'PET Panels', type: 'comparable', range: '100 – 240', width: '30%', offset: '12%' },
  { name: 'Wood Wool', type: 'comparable', range: '300 – 500', width: '25%', offset: '38%' },
  { name: 'MDF Panels', type: 'conventional', range: '600 – 750', width: '19%', offset: '75%' },
];

const TEST_RESULTS = [
  { property: 'Density', result: <span>224 kg/m<sup>3</sup> (Uncompressed)<br />367 kg/m<sup>3</sup> (Compressed)</span> },
  { property: 'Acoustic Screening', result: 'Sound attenuation up to 10 dB' },
  { property: 'Static Load Assessment', result: 'No visible deformation under a uniformly distributed static load of 30.45 kg' },
  { property: 'Water Absorption', result: '29% mass gain after 24H immersion', subtitle: '(% increase in weight after 24 hours)' },
];

const DensityBar = ({ name, type, range, width, offset }) => {
  const isConventional = type === 'conventional';
  return (
    <div className="flex items-center gap-3">
      <div className="w-36 md:w-48 text-right text-sm flex-shrink-0">
        <span className={isConventional ? 'text-[#5A6B5E]' : 'text-[#4A5D4E] font-medium'}>{name}</span>
        {isConventional && <span className="block text-xs text-[#D4A574]">(Conventional)</span>}
      </div>
      <div className="flex-1 bg-[#E8E3DA] rounded-full h-5 relative">
        <div
          className={`${isConventional ? 'bg-[#D4A574]/40' : 'bg-[#A8C9A5]/60'} h-5 rounded-full`}
          style={{ width, marginLeft: offset }}
        />
        <span className="absolute right-0 top-[-20px] text-xs text-[#5A6B5E]">{range}</span>
      </div>
    </div>
  );
};

export const TestingResults = () => (
  <div className="mb-16">
    <div className="text-center mb-10">
      <Badge className="bg-[#E8E3DA] text-[#4A5D4E] border-none mb-4">
        <Beaker className="w-3 h-3 mr-1" />
        Lab Validated
      </Badge>
      <h2 className="text-3xl font-bold text-[#2C3E2F] mb-4">
        Internal Testing Results
      </h2>
      <p className="text-[#5A6B5E] max-w-2xl mx-auto">
        Summary of preliminary internal tests conducted on our mycelium-based prototypes.
      </p>
    </div>

    {/* Results Table */}
    <div className="overflow-x-auto mb-10">
      <table className="w-full max-w-3xl mx-auto border-collapse">
        <thead>
          <tr>
            <th className="bg-[#2C3E2F] text-white px-6 py-4 text-left text-sm font-semibold rounded-tl-lg">Property</th>
            <th className="bg-[#2C3E2F] text-white px-6 py-4 text-left text-sm font-semibold rounded-tr-lg">Internal Result</th>
          </tr>
        </thead>
        <tbody>
          {TEST_RESULTS.map((row, i) => (
            <tr key={row.property} className={i % 2 === 0 ? 'bg-[#FDFBF7]' : 'bg-white'}>
              <td className={`px-6 py-4 text-[#2C3E2F] font-medium ${i < TEST_RESULTS.length - 1 ? 'border-b border-[#E8E3DA]' : ''}`}>
                {row.property}
                {row.subtitle && <><br /><span className="text-xs text-[#5A6B5E] font-normal">{row.subtitle}</span></>}
              </td>
              <td className={`px-6 py-4 text-[#5A6B5E] ${i < TEST_RESULTS.length - 1 ? 'border-b border-[#E8E3DA]' : ''}`}>
                {row.result}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Density Comparison */}
    <div className="bg-[#F5F3EE] rounded-2xl p-8 md:p-10 mb-10 max-w-4xl mx-auto">
      <h3 className="text-xl font-bold text-[#2C3E2F] mb-6 text-center">Density Comparison with Conventional Materials</h3>
      <div className="space-y-4">
        <div className="flex items-center text-xs text-[#5A6B5E] mb-2 pl-40 md:pl-52">
          <span>0</span>
          <span className="ml-auto">200</span>
          <span className="ml-auto">400</span>
          <span className="ml-auto">600</span>
          <span className="ml-auto">800</span>
        </div>
        {DENSITY_MATERIALS.slice(0, 3).map((m) => (
          <DensityBar key={m.name} {...m} />
        ))}
        {/* Punarvibes - highlighted */}
        <div className="flex items-center gap-3 bg-[#A8C9A5]/10 py-2 px-2 -mx-2 rounded-lg border border-[#A8C9A5]/30">
          <div className="w-36 md:w-48 text-right text-sm text-[#2C3E2F] font-bold flex-shrink-0">
            Punarvibes
            <span className="block text-xs font-normal text-[#4A5D4E]">(Mycelium Panels)</span>
          </div>
          <div className="flex-1 bg-[#E8E3DA] rounded-full h-6 relative">
            <div className="bg-[#A8C9A5] h-6 rounded-full flex items-center justify-center" style={{width: '46%', marginLeft: '28%'}}>
              <span className="text-white text-xs font-bold">224 – 367</span>
            </div>
          </div>
        </div>
        {DENSITY_MATERIALS.slice(3).map((m) => (
          <DensityBar key={m.name} {...m} />
        ))}
      </div>
      <p className="text-xs text-[#5A6B5E] text-center mt-6">
        Typical Density (kg/m<sup>3</sup>) — Punarvibes prototypes sit within the range of Cork, PET, and Wood Wool acoustic materials
      </p>
    </div>

    {/* Key Takeaways */}
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-start gap-3 bg-[#A8C9A5]/10 border border-[#A8C9A5]/30 rounded-xl p-5">
        <CheckCircle2 className="w-6 h-6 text-[#4A5D4E] flex-shrink-0 mt-0.5" />
        <p className="text-[#2C3E2F]">
          <span className="font-semibold">Density Comparable:</span> Our prototype densities are comparable with conventional materials like Cork, PET, and Wood Wool panels.
        </p>
      </div>
      <div className="flex items-start gap-3 bg-[#D4A574]/10 border border-[#D4A574]/30 rounded-xl p-5">
        <ArrowRight className="w-6 h-6 text-[#D4A574] flex-shrink-0 mt-0.5" />
        <p className="text-[#2C3E2F]">
          <span className="font-semibold">Next Phase:</span> Accredited third-party testing for compressive strength, flexural strength, density, water absorption, and acoustic performance. Building prototypes closer to commercial specifications.
        </p>
      </div>
    </div>
  </div>
);
